// /src/components/donate/DonateMonthlyFundingBar.jsx

import React, { useMemo, useState } from 'react';
import {
  computeMonthlyCostAmount,
  computeYearlyCostAmount,
  formatAmountWithSymbol,
} from '../../pages/private/admin/PrivateDocuments/donatePanel/donatePanelCostUtils';

const FUNDING_TABS = [
  { id: 'monthly', label: 'Monthly' },
  { id: 'yearly', label: 'Yearly' },
];

function buildFundingView({ raised, target, costRows, raisedLabel, targetLabel, progressAriaLabel }) {
  if (target <= 0) return null;

  const ratio = raised / target;
  const displayPercent = Math.round(ratio * 100);
  const isFullyFunded = raised >= target;
  const progressValue = Math.min(raised, target);

  return {
    progressValue,
    max: target,
    displayPercent,
    isFullyFunded,
    raisedLabel,
    targetLabel,
    donationLabel: formatAmountWithSymbol(costRows, raised),
    costLabel: formatAmountWithSymbol(costRows, target),
    progressAriaLabel,
  };
}

function FundingProgressPanel({ funding }) {
  const barClassName = [
    'donate-panel-member__funding-bar',
    funding.isFullyFunded ? 'donate-panel-member__funding-bar--complete' : '',
  ].filter(Boolean).join(' ');

  return (
    <>
      <div className="donate-panel-member__funding-track-wrap">
        <progress
          className={barClassName}
          value={funding.progressValue}
          max={funding.max}
          aria-label={funding.progressAriaLabel}
        />
      </div>

      <div className="donate-panel-member__funding-stats">
        <div className="donate-panel-member__funding-stat">
          <span className="donate-panel-member__funding-stat-label">{funding.raisedLabel}</span>
          <strong className="donate-panel-member__funding-stat-value donate-panel-member__funding-stat-value--raised">
            {funding.donationLabel}
          </strong>
        </div>
        <div className="donate-panel-member__funding-stat donate-panel-member__funding-stat--target">
          <span className="donate-panel-member__funding-stat-label">{funding.targetLabel}</span>
          <strong className="donate-panel-member__funding-stat-value">
            {funding.costLabel}
          </strong>
        </div>
      </div>
    </>
  );
}

export default function DonateMonthlyFundingBar({
  costRows,
  currentMonthDonationEur = 0,
  currentYearDonationEur = 0,
  className = '',
  idPrefix = '',
}) {
  const [activeTab, setActiveTab] = useState('monthly');

  const monthlyCost = useMemo(() => computeMonthlyCostAmount(costRows), [costRows]);
  const yearlyCost = useMemo(() => computeYearlyCostAmount(costRows), [costRows]);
  const monthlyDonations = Number.isFinite(currentMonthDonationEur) ? currentMonthDonationEur : 0;
  const yearlyDonations = Number.isFinite(currentYearDonationEur) ? currentYearDonationEur : 0;

  const monthlyView = useMemo(() => buildFundingView({
    raised: monthlyDonations,
    target: monthlyCost,
    costRows,
    raisedLabel: 'Raised this month',
    targetLabel: 'Monthly target',
    progressAriaLabel: `${formatAmountWithSymbol(costRows, monthlyDonations)} raised this month of ${formatAmountWithSymbol(costRows, monthlyCost)} monthly target`,
  }), [costRows, monthlyCost, monthlyDonations]);

  const yearlyView = useMemo(() => buildFundingView({
    raised: yearlyDonations,
    target: yearlyCost,
    costRows,
    raisedLabel: 'Raised this year',
    targetLabel: 'Yearly target',
    progressAriaLabel: `${formatAmountWithSymbol(costRows, yearlyDonations)} raised this year of ${formatAmountWithSymbol(costRows, yearlyCost)} yearly target`,
  }), [costRows, yearlyCost, yearlyDonations]);

  if (!monthlyView && !yearlyView) return null;

  const activeView = activeTab === 'yearly' ? yearlyView : monthlyView;
  const panelId = `${idPrefix}donate-funding-panel-${activeTab}`;
  const rootClassName = ['donate-panel-member__funding', className].filter(Boolean).join(' ');

  return (
    <section className={rootClassName} aria-label="Funding progress">
      <div className="donate-panel-member__funding-toolbar">
        <div
          className="donate-panel-member__funding-tabs"
          role="tablist"
          aria-label="Funding period"
        >
          {FUNDING_TABS.map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                id={`${idPrefix}donate-funding-tab-${tab.id}`}
                className={[
                  'donate-panel-member__funding-tab',
                  isActive ? 'is-active' : '',
                ].filter(Boolean).join(' ')}
                role="tab"
                aria-selected={isActive}
                aria-controls={panelId}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        {activeView ? (
          <span className="donate-panel-member__funding-percent">
            {activeView.displayPercent}%
          </span>
        ) : null}
      </div>

      {activeView ? (
        <div
          id={panelId}
          className="donate-panel-member__funding-panel"
          role="tabpanel"
          aria-labelledby={`${idPrefix}donate-funding-tab-${activeTab}`}
        >
          <FundingProgressPanel funding={activeView} />
        </div>
      ) : null}
    </section>
  );
}
